import {skipToken} from '@reduxjs/toolkit/query';
import {Button, Card, Flag, Separator} from 'platform/components';
import {Box, Grid, HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {
  useGetBusinessCaseQuery,
  useGetVehicleQuery,
  type DeclineOfferRequestBody,
} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {
  auctionDeadlines,
  auctionView,
  isAuctionAwarded,
  isOfferRejected,
} from '../../model/auctionView';
import type {AuctionOffer, AuctionView} from '../../model/auctionView.types';
import {ConfirmMaxPriceDialog} from '../components/ConfirmMaxPriceDialog';
import {LowRatingFlag} from '../components/LowRatingFlag';
import {RatingStars} from '../components/RatingStars';
import {useAuctionFormat} from '../components/useAuctionFormat';
import {type PurchaseCase} from '../components/usePurchaseCase';
import {
  Countdown,
  MaxPriceCalculation,
  OwnStockSeller,
  PartyHeading,
  PriceBlock,
} from './MaxPriceCalculation';
import {OfferDetailDialog} from './OfferDetailDialog';
import {PurchaseSellerCard} from './PurchaseSellerCard';
import {SaleCasePanel} from './SaleCasePanel';
import {TransactionStatus} from './TransactionStatus';
import {useConfirmWinner} from './useConfirmWinner';

interface ConfirmationStepProps {
  vehicleId: string;
  view: AuctionView;
  purchaseCase: PurchaseCase | null;
  buyer: AuctionOffer | null;
  onChangeSelection: () => void;
  onConfirm: (offerId: string) => Promise<AuctionView | null>;
  onDeclineOffer: (body: DeclineOfferRequestBody) => Promise<boolean>;
  onRequestSaleBusinessCase: () => void;
  now?: Date;
}

export function ConfirmationStep(props: ConfirmationStepProps) {
  const {data: vehicle} = useGetVehicleQuery({vehicleId: props.vehicleId});
  const format = useAuctionFormat();
  const {confirmWinner, notifyBuyer} = useConfirmWinner({
    vehicleId: props.vehicleId,
    vehicleTitle: vehicle?.title ?? '',
    view: props.view,
    isOwnStock: props.purchaseCase === null,
    onConfirm: props.onConfirm,
  });
  const now = props.now ?? new Date();
  const confirmAction = auctionView(props.view).actions.find(
    ({action}) => action === 'confirmWinner'
  );
  const isAwarded = isAuctionAwarded(props.view);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isMaxPriceOpen, setIsMaxPriceOpen] = useState(false);
  const isBuyerRejected = isOfferRejected(props.buyer);
  const vehiclePurchase = props.purchaseCase?.vehiclesPurchase[0] ?? null;
  const isBrokerage =
    vehiclePurchase === null && Boolean(props.purchaseCase?.vehiclesPurchaseBrokerage.length);
  // While bidding runs, the BE has no decision deadline yet. Project it from the bidding end.
  const deadline = props.view.publishedAt
    ? auctionDeadlines(props.view, now).decisionDeadlineAt.toISOString()
    : null;
  const {data: purchaseCaseDetail} = useGetBusinessCaseQuery(
    isNotNil(props.purchaseCase) ? {businessCaseId: props.purchaseCase.id} : skipToken
  );
  // The same field that the purchase case Buying and Pricing tabs read and write.
  const buying = purchaseCaseDetail?.offers[0]?.purchaseVehicles?.[0]?.buying ?? null;
  const maxBuyingPrice = buying?.maxBuyingPrice ?? null;

  return (
    <VStack spacing={4}>
      <SaleCasePanel
        vehicleId={props.vehicleId}
        view={props.view}
        onRequestSaleBusinessCase={props.onRequestSaleBusinessCase}
      />
      <Grid columns={[1, 1, 2]} spacing={4} data-testid="auction-confirmation">
        {props.purchaseCase === null ? (
          <OwnStockSeller vehicleId={props.vehicleId} />
        ) : (
          <PurchaseSellerCard
            view={props.view}
            purchaseCase={props.purchaseCase}
            buyer={props.buyer}
            now={now}
            maxBuyingPrice={maxBuyingPrice}
            isBrokerage={isBrokerage}
            onConfirmMaxPrice={() => setIsMaxPriceOpen(true)}
            onDeclineOffer={props.onDeclineOffer}
          />
        )}

        <Card variant="inlineWhite" isFullHeight data-testid="auction-confirmation-buyer">
          <Show when={props.buyer === null}>
            <Text color="secondary">{i18n.t('entity.auction.purchase.buyerNotSelected')}</Text>
          </Show>
          <Show when={props.buyer}>
            {(buyer) => (
              <VStack spacing={4} height="100%">
                <PartyHeading
                  icon="communication/business"
                  role={i18n.t('entity.auction.purchase.buyer')}
                  name={buyer.traderName}
                  meta={[buyer.traderCity, format.network(buyer.traderNetwork)]
                    .filter(Boolean)
                    .join(' · ')}
                  flag={
                    <Flag
                      label={i18n.t(
                        isBuyerRejected
                          ? 'entity.auction.offers.offerRejected'
                          : 'entity.auction.offers.selectedOffer'
                      )}
                      colorScheme={isBuyerRejected ? 'red' : 'blue'}
                      isSubtle
                      size="small"
                      data-testid="auction-confirmation-buyer-flag"
                    />
                  }
                />
                <Box padding={4} borderRadius="small" backgroundColor="palettes.neutral.10.100">
                  <HStack spacing={4} justify="space-between" wrap>
                    <PriceBlock
                      label={i18n.t(
                        isAwarded
                          ? 'entity.auction.handover.confirmedBid'
                          : 'entity.auction.confirmation.auctionOffer'
                      )}
                      value={format.money(props.view.transaction?.price ?? buyer.amount)}
                      tax={
                        vehicle?.isVatDeductible === false
                          ? i18n.t('entity.auction.confirmation.vatNotDeductible')
                          : null
                      }
                      testId="auction-confirmation-offer-price"
                      isLarge
                      isStruck={isBuyerRejected}
                    />
                    <Show when={!isAwarded && deadline}>
                      {(until) => (
                        <Countdown
                          until={until}
                          now={now}
                          caption={i18n.t('entity.auction.confirmation.decisionDeadline', {
                            date: format.dateTime(until),
                          })}
                          overdueLabel={i18n.t('entity.auction.header.decisionWindowEnded')}
                          testId="auction-confirmation-deadline"
                        />
                      )}
                    </Show>
                  </HStack>
                </Box>
                <VStack spacing={1} data-testid="auction-confirmation-rating">
                  <Text size="xSmall" color="secondary">
                    {i18n.t('entity.auction.offers.rating')}
                  </Text>
                  <HStack spacing={1} align="center">
                    <RatingStars rating={buyer.traderRating} />
                    <Text size="small" alternative>
                      {buyer.traderRating === null
                        ? i18n.t('entity.auction.offers.notRated')
                        : i18n.t('entity.auction.offers.ratingValue', {
                            rating: format.rating(buyer.traderRating),
                          })}
                    </Text>
                  </HStack>
                  <LowRatingFlag offer={buyer} testId="auction-confirmation-low-rating" />
                  <Show
                    when={
                      buyer.traderRatingsCount !== null || buyer.traderTransactionsCount !== null
                    }
                  >
                    <Text size="xSmall" color="secondary">
                      {[
                        buyer.traderRatingsCount === null
                          ? null
                          : format.count(
                              'entity.auction.offers.ratingsCount',
                              buyer.traderRatingsCount
                            ),
                        buyer.traderTransactionsCount === null
                          ? null
                          : format.count(
                              'entity.auction.offers.transactionsCount',
                              buyer.traderTransactionsCount
                            ),
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </Text>
                  </Show>
                </VStack>
                <Show when={!isAwarded}>
                  <VStack spacing={4} grow={1} justify="flex-end">
                    <Separator spacing={0} />
                    <Show when={isBuyerRejected}>
                      <HStack spacing={4} align="center" justify="space-between" wrap>
                        <Text
                          size="small"
                          color="danger"
                          data-testid="auction-confirmation-rejected"
                        >
                          {i18n.t('entity.auction.confirmation.offerRejected')}
                        </Text>
                        <Button
                          variant="primary"
                          title={i18n.t('entity.auction.confirmation.selectAnotherOffer')}
                          onClick={props.onChangeSelection}
                          data-testid="auction-confirmation-select-another-offer"
                        />
                      </HStack>
                    </Show>
                    <HStack spacing={4} align="center" justify="space-between" wrap>
                      <HStack spacing={4} align="center" wrap>
                        <Button
                          variant="link"
                          leftIcon="action/search"
                          title={i18n.t('entity.auction.preview.offerDetails')}
                          onClick={() => setIsDetailOpen(true)}
                          data-testid="auction-confirmation-offer-detail"
                        />
                        {/* shortcut: buyer chat is not built yet, so the link stays disabled. */}
                        <Button
                          variant="link"
                          leftIcon="communication/chat"
                          title={i18n.t('entity.auction.offers.messages')}
                          isDisabled
                          data-testid="auction-confirmation-messages"
                        />
                        <Button
                          variant="link"
                          leftIcon="action/swap_horiz"
                          title={i18n.t('entity.auction.confirmation.changeSelection')}
                          onClick={props.onChangeSelection}
                          data-testid="auction-confirmation-change-selection"
                        />
                      </HStack>
                      <Show when={confirmAction?.isShown && !isBuyerRejected}>
                        <Button
                          variant="primary"
                          title={i18n.t('entity.auction.confirmation.confirm')}
                          isDisabled={!confirmAction?.allowed}
                          onClick={() => props.buyer && confirmWinner(props.buyer)}
                          data-testid="auction-confirm-winner"
                        />
                      </Show>
                    </HStack>
                  </VStack>
                </Show>
                <Show when={props.view.transaction}>
                  {(transaction) => (
                    <VStack spacing={3}>
                      <Separator spacing={0} />
                      <TransactionStatus transaction={transaction} now={now} />
                      <Show
                        when={
                          isNotNil(transaction.saleBusinessCaseId) &&
                          (props.view.state === 'awaiting_vehicle' ||
                            props.view.state === 'awaiting_pickup')
                        }
                      >
                        <Button
                          variant="secondary"
                          leftIcon="communication/email"
                          title={i18n.t('entity.auction.notifyWinner.resend', {
                            defaultValue: 'Send winner notification again',
                          })}
                          onClick={() => void notifyBuyer(buyer)}
                          data-testid="auction-resend-winner-notification"
                        />
                      </Show>
                    </VStack>
                  )}
                </Show>
              </VStack>
            )}
          </Show>
        </Card>
      </Grid>
      <OfferDetailDialog
        offer={isDetailOpen ? props.buyer : null}
        roundNumber={props.view.roundNumber}
        isVatNotDeductible={vehicle?.isVatDeductible === false}
        onClose={() => setIsDetailOpen(false)}
      />
      <Show when={props.buyer !== null && props.purchaseCase !== null && !isAwarded}>
        <MaxPriceCalculation
          offer={props.buyer?.amount ?? null}
          maxBuyingPrice={maxBuyingPrice}
          purchaseCaseId={props.purchaseCase?.id ?? ''}
          isBrokerage={isBrokerage}
          onConfirm={() => setIsMaxPriceOpen(true)}
        />
      </Show>
      <Show when={isMaxPriceOpen && props.purchaseCase && props.buyer}>
        {(buyer) => (
          <ConfirmMaxPriceDialog
            isOpen
            purchaseCaseId={props.purchaseCase?.id ?? ''}
            vehicleId={props.vehicleId}
            offer={buyer.amount}
            buying={buying}
            onClose={() => setIsMaxPriceOpen(false)}
          />
        )}
      </Show>
    </VStack>
  );
}
